import { useEffect, useState, type ReactNode } from "react";
import { AuthContext, type AuthContextValue } from "./auth-context";
import { hasAuthHint, rememberedAuthUser } from "../lib/authHint";

/** What the prerendered landing was built with; the first client render must match. */
const SIGNED_OUT: AuthContextValue = { user: null, loading: false };

/** Run `fn` once the browser is idle (after first paint), cancellable. */
function whenIdle(fn: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(fn, { timeout: 3000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(fn, 1500);
  return () => window.clearTimeout(id);
}

/**
 * Auth for the public landing (/, /uz, /ru). Starts signed out, exactly like
 * the prerendered HTML, so hydration keeps the server DOM. Only when this
 * browser has signed in before (see lib/authHint) does it load the Firebase
 * auth SDK, after idle, to swap the header CTA to "Open OctoMail". First-time
 * visitors never download Firebase here.
 */
export function LandingAuthProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<AuthContextValue>(SIGNED_OUT);

  useEffect(() => {
    // Came here from the portal in this tab: it already knows who is signed in.
    const known = rememberedAuthUser();
    if (known !== undefined) setValue({ user: known, loading: false });
    if (!hasAuthHint()) return;
    let live = true;
    let unsubscribe: (() => void) | undefined;
    const cancel = whenIdle(() => {
      import("../lib/firebaseApp")
        .then(({ auth, onAuthStateChanged }) => {
          if (!live) return;
          unsubscribe = onAuthStateChanged(auth, (user) => setValue({ user, loading: false }));
        })
        .catch(() => {
          /* offline or stale chunk: keep the signed-out header */
        });
    });
    return () => {
      live = false;
      cancel();
      unsubscribe?.();
    };
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
